import useIdentity from "@/features/ui/hooks/use-identity";
import { Box, Flex } from "@chakra-ui/react";
import AppLineSheetTypeSelector from "@/features/line-sheet-sets/app-line-sheet-type-selector";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import { DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE } from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-pairs";
import { useEffect, useMemo, useRef } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useRecoilValue } from "recoil";
import { boutiqueOrderSheetSetRowsMutateId } from "@/features/order-status/recoils/boutique-order-sheet-set-rows-state";
import useBoutiqueOrderSheetSetRows from "@/features/order-status/hooks/use-boutique-order-sheet-set-rows";
import BoutiqueOrderSheetSetGrid from "@/features/order-status/boutique-order-sheet-set-grid";
import AppDateRangeSelect from "@/features/ui/app-date-range-select";
import AppListHeader from "@/features/ui/app-list-header";
import useListDenseOrderSheetSet, {
  ListDenseOrderSheetSetsPathParameter,
  ListDenseOrderSheetSetsRequest,
} from "@/features/order-sheet-sets/hooks/use-list-dense-order-sheet-set";
import { toTitle } from "@/utils/case";
import { useTranslation } from "react-i18next";
import { GridMapperToRowOption } from "@/features/ui/grid-row/gird-mapper";
import AppSeasonMultiSelect from "@/features/line-sheet-sets/app-season-multi-select";
import { OrderByItem } from "@/features/types";
import { ListOrderSheetSetsRequest } from "@/features/line-sheet-sets/hooks/use-list-order-sheet-sets";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";

export interface PersistedOrderSheetSetListQuery
  extends Partial<ListOrderSheetSetsRequest> {}

export default function BoutiqueOrderSheetSetListPage() {
  const identity = useIdentity();
  const rowsMutateId = useRecoilValue(boutiqueOrderSheetSetRowsMutateId);
  const { error: showError, warning: showWarning } = useAppToasts();
  const { setRows, mutateRows } = useBoutiqueOrderSheetSetRows();
  const { t } = useTranslation();
  const [query, setQuery] =
    useTypedSearchParams<PersistedOrderSheetSetListQuery>();
  const toRowsOptionRef = useRef<GridMapperToRowOption | undefined>(undefined);

  // --- API: List Order Sheet Sets ---

  const listOrderSheetSetsKey = useMemo(():
    | {
        parameter: ListDenseOrderSheetSetsPathParameter;
        request: ListDenseOrderSheetSetsRequest;
      }
    | undefined => {
    const { pageNumber, ...rest } = query;

    let q: Partial<ListDenseOrderSheetSetsRequest> = {
      ...rest,
    };

    // if (status__eq === undefined) {
    //   q.status__in = OrderSheetStatuses.filter(
    //     (status) => status !== "WORKING_DRAFT" && status !== "INITIAL_DRAFT"
    //   );
    // } else {
    //   q.status__eq = status__eq;
    // }

    if (identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          ...q,
          pageNumber: pageNumber || 1,
          pageSize: DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE,
          orderBy: [new OrderByItem("submittedAt", "descend")],
        },
      };
    }
  }, [identity, query]);

  const {
    isLoading: isListOrderSheetSetsLoading,
    data: listOrderSheetSetsData,
    error: listOrderSheetSetsError,
    mutate: mutateListOrderSheetSets,
  } = useListDenseOrderSheetSet(
    listOrderSheetSetsKey?.parameter,
    listOrderSheetSetsKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );


  useEffect(() => {
    setRows(listOrderSheetSetsData?.orderSheetSets, toRowsOptionRef.current);
    toRowsOptionRef.current = undefined;
  }, [listOrderSheetSetsData, setRows]);

  useEffect(() => {
    if (rowsMutateId > 0) {
      mutateListOrderSheetSets();
    }
  }, [mutateListOrderSheetSets, rowsMutateId]);

  // --- END: API: List Order Sheet Sets ---

  return (
    <>
      <Flex
        flexDirection={"column"}
        gap={"12px"}
        height={"100%"}
        paddingBottom={"16px"}
      >
        <AppListHeader
          filters={[
            <AppLineSheetTypeSelector
              key={"BoutiqueOrderSheetSetListFilter_Type"}
              value={query["lineSheetSet.type__eq"]}
              onSelect={(value) =>
                setQuery((prev) => ({
                  ...prev,
                  "lineSheetSet.type__eq": value,
                  pageNumber: 1,
                }))
              }
            />,
            <AppSeasonMultiSelect
              key={"season"}
              value={query["lineSheetSet.season__in"]}
              onSelect={(value) =>
                setQuery((prev) => ({
                  ...prev,
                  "lineSheetSet.season__in": value,
                  pageNumber: 1,
                }))
              }
            />,
            <AppCompanySelect
              key={"BoutiqueOrderSheetSetListFilter_OrderedBy"}
              name={toTitle(t("ordered_by"))}
              width={"144px"}
              types={["BUYER"]}
              id={query["createdByCompany.id__eq"]}
              onSelect={(company) =>
                setQuery((prev) => ({
                  ...prev,
                  "createdByCompany.id__eq": company?.id,
                  pageNumber: 1,
                }))
              }
            />,
            // <AppOrderSheetSetStatusSelector
            //   key={"BoutiqueOrderSheetSetListFilter_Status"}
            //   width={"144px"}
            //   value={query.status__eq}
            //   excludeStatuses={["WORKING_DRAFT", "INITIAL_DRAFT"]}
            //   onSelect={(v) => patchQuery("status__eq", v)}
            // />,
            <AppDateRangeSelect
              key={"BoutiqueOrderSheetSetListFilter_OrderedOn"}
              width={"216px"}
              name={toTitle(t("ordered_on"))}
              value={{
                from: query.submittedAt__gte,
                to: query.submittedAt__lte,
              }}
              onChange={(value) =>
                setQuery((prev) => ({
                  ...prev,
                  submittedAt__gte: value?.from,
                  submittedAt__lte: value?.to,
                  pageNumber: 1,
                }))
              }
            />,
            <AppButton
              key={"BoutiqueOrderSheetSetListFilter_ResetFilter"}
              variant={"normal"}
              size={"medium"}
              width={"144px"}
              onClick={() => {
                setQuery((prev) => {
                  return {
                    pageNumber: 1,
                  };
                });
              }}
            >
              {toTitle(t("reset_filter"))}
            </AppButton>,
          ]}
          // 부티크 주문시트 목록에서는 상태를 바꾸지 않는다(오더 상세에서 한다)
          actions={[]}
        />
        <Box
          flexGrow={1}
          overflow={"auto"}
          style={{
            scrollbarGutter: "stable",
          }}
        >
          <BoutiqueOrderSheetSetGrid />
        </Box>

        <Flex flexDirection={"row"} justifyContent={"center"}>
          <AppPagination
            capacity={10}
            pageSize={DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE}
            totalCount={listOrderSheetSetsData?.totalCount}
            pageNumber={query.pageNumber}
            setPageNumber={(value) =>
              setQuery((prev) => ({ pageNumber: value }))
            }
          />
        </Flex>
      </Flex>
    </>
  );
}
